import { useCallback, useEffect, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { CaCard } from '../certificates/ca-card.jsx';
import { CertTable } from '../certificates/cert-table.jsx';
import { IssueCertModal } from '../certificates/issue-modal.jsx';
import { IssuedBundleModal } from '../certificates/issued-bundle-modal.jsx';
import { RevokeCertModal } from '../certificates/revoke-modal.jsx';
import { CertDetailModal } from '../certificates/cert-detail-modal.jsx';
import { api, ApiError } from '../../api/index.js';

const STATUS_TABS = [
  { id: 'all',     label: 'All',     icon: 'fa-list' },
  { id: 'active',  label: 'Active',  icon: 'fa-circle-check' },
  { id: 'expired', label: 'Expired', icon: 'fa-hourglass-end' },
  { id: 'revoked', label: 'Revoked', icon: 'fa-ban' },
];

export function CertificatesPage({ currentUser }) {
  const toast = useToast();
  const [ca, setCa] = useState(null);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(id);
  }, [searchInput]);

  const [reasons, setReasons] = useState([]);

  const [issueState, setIssueState] = useState({ open: false, mode: 'issue', cert: null });
  const [bundleState, setBundleState] = useState({ open: false, cert: null, bundle: null });
  const [revokeState, setRevokeState] = useState({ open: false, cert: null });
  const [detailState, setDetailState] = useState({ open: false, cert: null });
  const [deleteState, setDeleteState] = useState({ open: false, cert: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [caRes, listRes] = await Promise.all([
        api.ca.info(),
        api.certificates.list({
          status: statusFilter === 'all' ? '' : statusFilter,
          q: search,
          limit: 200,
        }),
      ]);
      setCa(caRes.ca);
      setStats(caRes.stats);
      setData(listRes);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.certificates.reasons().then((r) => setReasons(r.reasons || [])).catch(() => {});
  }, []);

  async function handleIssueSubmit(payload) {
    if (issueState.mode === 'renew' && issueState.cert) {
      const res = await api.certificates.renew(issueState.cert.id, payload);
      setIssueState({ open: false, mode: 'issue', cert: null });
      setBundleState({ open: true, cert: res.certificate, bundle: res.bundle });
      toast.success(`Certificate "${res.certificate.subjectCn}" renewed.`);
    } else {
      const res = await api.certificates.issue(payload);
      setIssueState({ open: false, mode: 'issue', cert: null });
      setBundleState({ open: true, cert: res.certificate, bundle: res.bundle });
      toast.success(`Certificate "${res.certificate.subjectCn}" issued.`);
    }
    await refresh();
  }

  async function handleRevoke(reason) {
    const id = revokeState.cert?.id;
    if (!id) return;
    await api.certificates.revoke(id, reason);
    toast.success(`Certificate "${revokeState.cert.subjectCn}" revoked.`);
    setRevokeState({ open: false, cert: null });
    await refresh();
  }

  async function handleDelete() {
    try {
      await api.certificates.remove(deleteState.cert.id);
      toast.success(`Certificate "${deleteState.cert.subjectCn}" deleted from history.`);
      setDeleteState({ open: false, cert: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  return (
    <PageShell
      icon="fa-shield-halved"
      title="Certificates"
      subtitle="Identity authority — issue, renew, and revoke node certificates"
      actions={
        <button
          type="button"
          onClick={() => setIssueState({ open: true, mode: 'issue', cert: null })}
          className="btn-primary"
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
          Issue certificate
        </button>
      }
    >
      <div className="space-y-6">
        <CaCard
          ca={ca}
          stats={stats}
          viewerTimezone={currentUser?.timezone || 'UTC'}
          onDownloadPem={api.ca.caCertUrl}
          onDownloadCrlPem={api.ca.crlPemUrl}
          onDownloadCrlDer={api.ca.crlDerUrl}
        />

        <div className="card flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setStatusFilter(t.id)}
                className={
                  statusFilter === t.id
                    ? 'flex items-center gap-2 rounded-xl bg-accent-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent'
                    : 'flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
                }
              >
                <i className={`fa-solid ${t.icon} text-[11px]`} aria-hidden="true" />
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:max-w-xs">
            <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
            <input
              type="search"
              className="field pl-10"
              placeholder="Subject, serial, fingerprint, node…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div role="alert" className="card border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="card px-6 py-16 text-center text-mist">
            <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
            <div className="text-sm">Loading certificates…</div>
          </div>
        ) : (
          <CertTable
            rows={data?.items || []}
            viewerTimezone={currentUser?.timezone || 'UTC'}
            onView={(c) => setDetailState({ open: true, cert: c })}
            onRenew={(c) => setIssueState({ open: true, mode: 'renew', cert: c })}
            onRevoke={(c) => setRevokeState({ open: true, cert: c })}
            onDelete={(c) => setDeleteState({ open: true, cert: c })}
            onDownload={(c) => api.certificates.pemUrl(c.id)}
          />
        )}
      </div>

      <IssueCertModal
        open={issueState.open}
        mode={issueState.mode}
        certificate={issueState.cert}
        onClose={() => setIssueState({ open: false, mode: 'issue', cert: null })}
        onSubmit={handleIssueSubmit}
      />

      <IssuedBundleModal
        open={bundleState.open}
        certificate={bundleState.cert}
        bundle={bundleState.bundle}
        onClose={() => setBundleState({ open: false, cert: null, bundle: null })}
      />

      <RevokeCertModal
        open={revokeState.open}
        certificate={revokeState.cert}
        reasons={reasons}
        onClose={() => setRevokeState({ open: false, cert: null })}
        onConfirm={handleRevoke}
      />

      <CertDetailModal
        open={detailState.open}
        certificate={detailState.cert}
        viewerTimezone={currentUser?.timezone || 'UTC'}
        downloadHref={detailState.cert ? api.certificates.pemUrl(detailState.cert.id) : '#'}
        onClose={() => setDetailState({ open: false, cert: null })}
      />

      <ConfirmDialog
        open={deleteState.open}
        title={`Delete certificate?`}
        message={
          deleteState.cert
            ? `This permanently removes the "${deleteState.cert.subjectCn}" certificate record from history. The audit entry remains. CRL is unaffected.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, cert: null })}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
}
